import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
} from "@mui/material";
import { MoreVert as MoreIcon, Lens } from "@mui/icons-material";
import TableDesign, {
  TableHeading,
} from "../../../../components/common/table/TableDesign";
import PageTitle from "../../../../components/pageTitle/PageTitle";
import ManageUnitType from "./ManageUnitType";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllUnitTypesAction,
  setAddUnitTypeResponse,
  setDeleteUnitTypeResponse,
  deleteUnitTypeAction,
} from "../../../../store/reducers/storageSettingSlice";
import DeleteAlert from "../../../../components/common/DeleteAlert";

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
}

const DEFAULT_FILTER_OPTION: FilterOption = {
  page: 1,
  limit: 10,
  orderBy: "created_at",
  orderIn: "DESC",
  search: "",
};

const TABLE_HEADINGS: TableHeading[] = [
  { label: "#", align: "left" },
  { label: "Type Name", align: "left" },
  { label: "Storage Type", align: "left" },
  { label: "Size", align: "left" },
  { label: "Quantity", align: "left" },
  { label: "Price/Unit", align: "left" },
  { label: "Action", align: "center" },
];

const UnitSummary = () => {
  const dispatch = useDispatch();
  const {
    unitTypeSummarys,
    pagination,
    isAddUnitTypeSuccess,
    isDeleteResponseSuccess,
    isUpdateUnitTypeSuccess,
  } = useSelector((state: any) => state.storageSetting);

  const [filterOption, setFilterOption] = useState<FilterOption>(
    DEFAULT_FILTER_OPTION,
  );
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleOpenAdd = () => {
    setSelectedRow(null);
    setTitle("Add Unit Type");
    setOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditOpen = () => {
    setTitle("Edit Unit Type");
    setOpen(true);
    handleMenuClose();
  };

  // Only closes the menu — selectedRow must stay set so the
  // confirmation popup knows which row to delete.
  const handleDeleteOpen = () => {
    setIsDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleFormModalClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmOpen(false);
    setSelectedRow(null);
  };

  useEffect(() => {
    dispatch(getAllUnitTypesAction(filterOption));
    dispatch(setAddUnitTypeResponse(false));
    dispatch(setDeleteUnitTypeResponse(false));

    handleFormModalClose();
  }, [filterOption, dispatch, isAddUnitTypeSuccess, isUpdateUnitTypeSuccess]);

  useEffect(() => {
    if (isDeleteResponseSuccess) {
      dispatch(getAllUnitTypesAction(filterOption));
      setIsDeleteConfirmOpen(false);
    }
    setSelectedRow(null);
  }, [isDeleteResponseSuccess]);

  const handleConfirmDelete = useCallback(() => {
    if (selectedRow?.id) {
      dispatch(deleteUnitTypeAction(selectedRow.id));
    }
  }, [dispatch, selectedRow]);

  return (
    <>
      <PageTitle
        title="Unit Summary"
        button="Add New Unit"
        onClick={handleOpenAdd}
      />
      <Box
        sx={{
          bgcolor: "#fff",
          height: { xs: "auto", sm: "60vh" },
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
          padding: "0px",
          overflow: "hidden",
        }}
      >
        <TableDesign
          tableHeadings={TABLE_HEADINGS}
          tableHeadColor={"#02906f"}
          cellColor={"#fff"}
          pagination={unitTypeSummarys.length > 0 ? pagination : null}
          isLoader={false}
          stickyHeader={true}
          filterOption={filterOption}
          setFilterOption={setFilterOption}
          extraCSS={{
            height: "calc(100%)",
          }}
        >
          {unitTypeSummarys.length > 0 ? (
            unitTypeSummarys?.map((val: any, idx: number) => (
              <TableRow
                key={val?.id}
                sx={{
                  cursor: "pointer",
                  marginTop: "10px",
                  padding: "4px !important",
                  borderRadius: "20px !important",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    outline: "2px solid #f0f0f0",
                    borderRadius: "0px !important",
                    outlineOffset: "-2px !important",
                  },
                }}
              >
                <TableCell align="left">{idx + 1}</TableCell>
                <TableCell>{val?.unit_type_name}</TableCell>
                <TableCell>{val?.storage_type}</TableCell>
                <TableCell align="left">{val?.size_ft3}</TableCell>
                <TableCell align="left">{val?.quantity}</TableCell>
                <TableCell align="left">£ {val?.price_per_unit}</TableCell>
                <TableCell align="center" sx={{ padding: "0px" }}>
                  <IconButton
                    sx={{ padding: "0px" }}
                    size="small"
                    id="basic-button"
                    aria-controls={isMenuOpen ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen ? "true" : undefined}
                    onClick={(e) => handleMenuOpen(e, val)}
                  >
                    <MoreIcon />
                  </IconButton>
                  <Menu
                    sx={{
                      boxShadow: "none",
                      border: "2px solid #eeecef",
                      "& .MuiPaper-root": {
                        boxShadow: "none",
                        border: "2px solid #eeecef",
                      },
                      top: "0px",
                    }}
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                  >
                    <MenuItem onClick={handleEditOpen}>
                      <Lens
                        style={{
                          height: "4px",
                          width: "4px",
                          marginRight: "4px",
                        }}
                      />
                      Edit
                    </MenuItem>

                    <MenuItem onClick={handleDeleteOpen}>
                      <Lens
                        style={{
                          height: "4px",
                          width: "4px",
                          marginRight: "4px",
                        }}
                      />
                      Delete
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <Box
                  sx={{
                    height: "60vh",
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      fontSize: "1rem",
                      color: "#4A4A4A",
                      top: "100px",
                    }}
                  >
                    No Materials Found.
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableDesign>
      </Box>

      {open && (
        <ManageUnitType
          open={open}
          onHide={handleFormModalClose}
          selectData={selectedRow}
          title={title}
          isSmallScreen={false}
        />
      )}

      {isDeleteConfirmOpen && (
        <DeleteAlert
          open={isDeleteConfirmOpen}
          onHide={handleDeleteConfirmClose}
          title="Delete Service"
          message="Are you sure you want to delete this unit type?"
          alertModalShow={isDeleteConfirmOpen}
          setAlertModelShow={setIsDeleteConfirmOpen}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

export default UnitSummary;
